import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { environment } from '../../../../environments/environment';
import { IrrigationSupplyGet, IrrigationSupportedCommodityModel } from '../../_models';
import { GetIrrigationAdviceParams } from '../../_models/irrigation_v2/irrigationServiceParams';
import { IrrigationStrategy, Strategy } from '../../_models/irrigation_v2/irrigationStrategiesGet';
import {
  IrrigationHistoryEtc,
  IrrigationHistoryEtcGetParams,
} from '../../_models/irrigation_v2/irrigationHistoryEtcGet';
import {
  IrrigationCropDevices,
  PutIrrigationCropDevicesParams,
  PutIrrigationSensorParams,
} from '../../_models/irrigation_v2/irrigationCropDevice';

@Injectable({
  providedIn: 'root',
})
export class XapiIrrigationService {
  private apiBaseUrl = `${environment.apiBaseUrl}`;
  private apiIrrigationServiceUrl = `${environment.apiGatewayUrl}irrigation-service/v1`;

  private cropDevicesSubject = new BehaviorSubject<IrrigationCropDevices | null>(null);

  constructor(private http: HttpClient) {}

  // API Calls for irrigation
  getIrrigationSupportedCommodity(): Observable<IrrigationSupportedCommodityModel[]> {
    return this.http.get<IrrigationSupportedCommodityModel[]>(
      `${this.apiIrrigationServiceUrl}/irrigationCommodities`
    );
  }

  // Method needed to get commodities if they are stored, otherwise it will call fetchCommodities
  getIrrigationAdvice(params: GetIrrigationAdviceParams): Observable<IrrigationSupplyGet> {
    const { farmId, cropYear, commodityId } = params;
    let queryParameters = new HttpParams({ fromObject: { farmId, cropYear } });
    if (commodityId) {
      queryParameters = queryParameters.set('commodityId', commodityId);
    }

    return this.http
      .get<IrrigationSupplyGet>(`${this.apiIrrigationServiceUrl}/irrigationAdvices`, {
        params: queryParameters,
      })
      .pipe(
        tap(({ commodities }) => {
          return commodities;
        })
      );
  }

  // Method needed to get irrigation strategies
  getIrrigationStrategies(cropId: number): Observable<IrrigationStrategy> {
    return this.http
      .get<IrrigationStrategy>(
        `${this.apiIrrigationServiceUrl}/irrigationStrategy?cropId=${cropId}`
      )
      .pipe(
        tap((response) => {
          return response;
        })
      );
  }

  // Method needed to put irrigation strategies
  setIrrigationStrategies(cropId: number, body: Strategy): Observable<IrrigationStrategy> {
    const queryParameters = new HttpParams({ fromObject: { cropId } });

    return this.http
      .put<IrrigationStrategy>(`${this.apiIrrigationServiceUrl}/irrigationStrategy`, body, {
        params: queryParameters,
      })
      .pipe(
        tap((response) => {
          return response;
        })
      );
  }

  // Method needed to get irrigation history etc
  getIrrigationHistoryEtc(
    params: IrrigationHistoryEtcGetParams,
    companyId: number
  ): Observable<IrrigationHistoryEtc> {
    const { cropId, startDate, endDate, lat, long } = params;

    let queryParameters = new HttpParams().set('cropId', cropId.toString());
    queryParameters = queryParameters.set('companyId', companyId.toString());
    if (startDate) {
      queryParameters = queryParameters.set('startDate', startDate);
    }
    if (endDate) {
      queryParameters = queryParameters.set('endDate', endDate);
    }
    if (lat) {
      queryParameters = queryParameters.set('lat', lat);
    }
    if (long) {
      queryParameters = queryParameters.set('long', long);
    }

    return this.http
      .get<IrrigationHistoryEtc>(`${this.apiIrrigationServiceUrl}/irrigationHistoryEtc`, {
        params: queryParameters,
      })
      .pipe(
        tap((response) => {
          return response;
        })
      );
  }

  getCropDevices(cropId: number, forceRefresh = false): Observable<IrrigationCropDevices> {
    const shouldFetch = forceRefresh || !this.cropDevicesSubject.value;

    if (shouldFetch) {
      return this.http
        .get<IrrigationCropDevices>(`${this.apiBaseUrl}api/private/v1/crops/devices/${cropId}`)
        .pipe(tap((res) => this.cropDevicesSubject.next(res)));
    }

    return this.cropDevicesSubject.asObservable().pipe(
      filter((val): val is IrrigationCropDevices => val !== null),
      take(1)
    );
  }

  // Assign devices to a crop
  setCropDevices(cropId: number, params: PutIrrigationCropDevicesParams): Observable<void> {
    return this.http
      .put<void>(`${this.apiBaseUrl}api/private/v1/crops/devices/${cropId}`, params)
      .pipe(map((response) => response));
  }

  // Put to change sensor parameters
  setSensorParams(cropId: number, params: PutIrrigationSensorParams): Observable<void> {
    return this.http
      .put<void>(`${this.apiBaseUrl}api/private/v1/crops/sensor/${cropId}`, params)
      .pipe(map((response) => response));
  }
}
